import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { ChevronDown, Search } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Field, { type FieldProps } from '@/components/common/Field';
import InputGroup from '@/components/common/Input/InputGroup';

export const defaultRenderOption = (option: SelectOption) => (
    <>
        <span className="block truncate">{option.label}</span>
        <span className="text-primary group-data-focus:text-primary-foreground absolute inset-y-0 right-0 flex items-center pr-4 group-[&:not([data-selected])]:hidden">
            <CheckIcon aria-hidden="true" className="size-5" />
        </span>
    </>
);

export interface SelectOption {
    value: string | number | null;
    label: string;
    [key: string]: any;
}

interface BaseProps extends Omit<FieldProps, 'id' | 'children'> {
    id?: string;
    options: SelectOption[];
    inputState?: 'valid' | 'invalid' | 'normal';
    renderOption?: (option: SelectOption) => ReactNode;
    name: string;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
}

type ModeProps =
    | { multiple?: false; value?: SelectOption['value']; onChange?: (value: SelectOption['value']) => void }
    | { multiple: true; value?: SelectOption['value'][]; onChange?: (value: SelectOption['value'][]) => void };

export type SelectProps = BaseProps & ModeProps;

type ListboxValue = SelectOption['value'] | SelectOption['value'][] | null;

const useListBox = (props: ModeProps) => {
    if (props.multiple) {
        return {
            multiple: true,
            value: props.value,
            onChange: (value: ListboxValue) => {
                if (Array.isArray(value)) {
                    props.onChange?.(value);
                }
            },
        };
    }

    return {
        multiple: false,
        value: props.value,
        onChange: (value: ListboxValue) => {
            if (!Array.isArray(value)) {
                props.onChange?.(value);
            }
        },
    };
};

const Select = ({
    id,
    label,
    options,
    renderOption = defaultRenderOption,
    helpElement,
    inputState = 'normal',
    errorElement,
    name,
    placeholder,
    disabled = false,
    required,
    searchable = false,
    searchPlaceholder = 'Search...',
    ...props
}: SelectProps) => {
    const elementId = useId();
    const computedId = id || elementId;
    const { t } = useTranslation('common');
    const [searchTerm, setSearchTerm] = useState('');

    const listBoxProps = useListBox(props);

    const { value } = props;
    const selectedOptions = useMemo(() => {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return options.filter(option => value.includes(option.value));
        }

        if (!options.find(option => option.value === value)) {
            return [];
        }

        return [options.find(option => option.value === value)!];
    }, [options, value]);

    // Filter options based on search term when searchable is enabled
    const filteredOptions = useMemo(() => {
        if (!searchable || !searchTerm) {
            return options;
        }
        return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchable, searchTerm]);

    const isPlaceholder = !selectedOptions || selectedOptions.length === 0;

    const optionLabel =
        (selectedOptions.length === 1 && selectedOptions[0].label) ||
        (selectedOptions.length > 1 && selectedOptions.map(option => option.label).join(', ')) ||
        placeholder ||
        (props.multiple ? t('common:select.placeholderMultiple') : t('common:select.placeholderSingle'));

    return (
        <Listbox {...listBoxProps}>
            {({ open }) => (
                <Field
                    name={name}
                    label={label}
                    errorElement={errorElement}
                    helpElement={helpElement}
                    id={computedId}
                    required={required}
                    description={props.description}
                >
                    <ListboxButton
                        as={InputGroup}
                        disabled={disabled}
                        inputState={inputState}
                        data-cy="select"
                        data-cy-id={computedId}
                        data-empty={!selectedOptions.length || undefined}
                        className={clsx(
                            'data-empty:text-muted-foreground data-disabled:cursor-not-allowed',
                            disabled ? 'text-muted-foreground bg-neutral-100' : ''
                        )}
                    >
                        <span
                            className={`w-full grow truncate py-[7px] pr-1 text-base font-medium ${isPlaceholder || disabled ? 'text-muted-foreground' : 'text-neutral-900'}`}
                        >
                            {optionLabel}
                        </span>
                        {!disabled && (
                            <span className="shrink-0 text-base text-neutral-900 select-none">
                                <ChevronDown
                                    aria-hidden="true"
                                    className={clsx('size-5 transition-transform', open && 'rotate-180')}
                                />
                            </span>
                        )}
                    </ListboxButton>

                    <ListboxOptions
                        anchor="bottom start"
                        className={clsx(
                            // position
                            'absolute z-40 mt-1 max-h-56 overflow-auto',
                            // base — matches Dropdown/PopoverMenu (bg-popover, shadow-sm, no ring) per Figma
                            'bg-popover rounded-[10px] p-[5px] text-base font-medium text-neutral-900 shadow-sm focus:outline-none',
                            // transitions
                            'data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in data-[closed]:data-[leave]:opacity-0'
                        )}
                        data-cy="options"
                        data-cy-id={computedId}
                        transition
                    >
                        {/* Search Input - only show when searchable is true */}
                        {searchable && (
                            <div className="bg-popover sticky top-0 border-b border-neutral-300 px-3 py-2">
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="placeholder:text-muted-foreground focus:ring-primary w-full rounded-md border border-neutral-300 bg-transparent py-1 pr-8 pl-3 text-base font-medium text-neutral-900 focus:border-transparent focus:ring-2 focus:outline-none"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                    <Search
                                        className="text-muted-foreground absolute top-1/2 right-2 size-4 -translate-y-1/2 transform"
                                        strokeWidth={2}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options Container */}
                        <div className="max-h-48 overflow-y-auto py-1">
                            {filteredOptions.length === 0 ? (
                                <div className="text-muted-foreground relative cursor-default px-4 py-2 select-none">
                                    {searchable && searchTerm ? 'No options found.' : 'No options available.'}
                                </div>
                            ) : (
                                <div className="py-1">
                                    {filteredOptions.map(option => (
                                        <ListboxOption
                                            key={option.value}
                                            value={option.value}
                                            className={clsx(
                                                // base
                                                'group relative cursor-default rounded-md px-2 py-1.5 pr-9 text-base text-neutral-900 select-none',
                                                // focus
                                                'data-focus:bg-primary data-focus:text-primary-foreground data-focus:outline-none',
                                                // selected (non-focused) state — subtle neutral highlight; the brand
                                                // signal comes from the text-primary check icon
                                                selectedOptions.some(selected => selected.value === option.value) &&
                                                    'bg-neutral-100'
                                            )}
                                        >
                                            {renderOption(option)}
                                            {selectedOptions.some(selected => selected.value === option.value) && (
                                                <span className="text-primary absolute inset-y-0 right-0 flex items-center pr-4 text-base">
                                                    <CheckIcon className="size-5" aria-hidden="true" />
                                                </span>
                                            )}
                                        </ListboxOption>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ListboxOptions>
                </Field>
            )}
        </Listbox>
    );
};

export default Select;
